import { SEARCH_FILTERS_GROUP } from "../../../../config/search/filters/groupsConfig";
import useSearchTableControl from "../../../../hooks/portal/search/useSearchTableControl";
import { deleteReadFeedbacks, selectFeedbacks } from "../../../../axios/api/services";
import {
  AsyncCallArgs,
  BorderGreyBox,
  CardSpg,
  CreatedDateFilter,
  DividerSpg,
  handleSubmitValidationFail,
  isArrayNotEmpty,
  isNotEmpty,
  NameFilter,
  SecurityGuard,
  SecurityRole,
  useAsyncCall,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import { Box, Button, CardContent, Grid } from "@mui/material";
import SearchFilters from "../../common/search/filters/SearchFilters";
import FeedbackList from "./FeedbackList";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FormProvider } from "react-hook-form";
import { FeedbackFilterDetails } from "../../../../types/feedbackTypes";
import { feedbackFilterInitialValues } from "../../../../init/feedbackFilterInitialValues";
import { createFeedbackFilterValidationSchema } from "../../../../yup/schema/feedback/feedbackFilterValidationSchema";
import { useEffect, useMemo, useState } from "react";
import { useStore } from "react-redux";
import * as React from "react";
import { RootState } from "../../../../store/redux/store";

const FeedbackFilters = () => {
  return (
    <Grid container spacing={5} px={2} py={6}>
      <NameFilter />
      <CreatedDateFilter />
    </Grid>
  );
};

const Feedback = () => {
  const { t } = useTranslation();
  const store = useStore();
  const { asyncCall } = useAsyncCall();
  const group = SEARCH_FILTERS_GROUP.FEEDBACK_MANAGEMENT;
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);

  const { methods } = useReactHookForm<FeedbackFilterDetails>({
    defaultValues: feedbackFilterInitialValues,
    validationSchema: createFeedbackFilterValidationSchema,
  });

  useEffect(() => {
    const filtersData = (store.getState() as RootState).SearchData.portalSearchTable[group].filtersData;
    if (isNotEmpty(filtersData)) {
      methods.reset(filtersData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const control = useSearchTableControl({
    group,
    methods,
    initialValues: feedbackFilterInitialValues,
    filterData: selectFeedbacks,
    callAfterResetFilters: true,
  });

  const handleDeleteRead = () => {
    setIsDeleteSubmitting(true);
    const asyncFeedbackDeleteRead: AsyncCallArgs = {
      promise: deleteReadFeedbacks(),
      onSuccess: () => {
        control.fetch();
        toast.success(t("m.feedback.delete.read.success"));
        setIsDeleteSubmitting(false);
      },
      onError: () => {
        setIsDeleteSubmitting(false);
      },
    };
    asyncCall(asyncFeedbackDeleteRead);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(
          () => control.handleSubmitFilters(),
          () => handleSubmitValidationFail(methods.formState, t),
        )}
      >
        <CardSpg my={4} style={{ overflow: "visible" }}>
          <CardContent style={{ position: "relative" }}>
            <Box>
              <BorderGreyBox>
                <SearchFilters
                  isSubmitBtnDisabled={control.isSubmitBtnDisabled}
                  isResetBtnDisabled={control.isResetBtnDisabled}
                  handleResetFilters={control.handleResetFilters}
                >
                  {useMemo(
                    () => (
                      <FeedbackFilters />
                    ),
                    [],
                  )}
                </SearchFilters>
              </BorderGreyBox>
              {control.showList && (
                <FeedbackList
                  records={control.records}
                  total={control.total}
                  isLoading={control.isLoading}
                  onPageOrOrderChange={control.handlePageOrOrderChange}
                  blockTable={control.blockTable}
                  group={group}
                  fetch={control.fetch}
                />
              )}
              <SecurityGuard checkForRoles={[SecurityRole.AcFeedbacksDelete]}>
                {isArrayNotEmpty(control.records) && (
                  <>
                    <DividerSpg my={4} />
                    <Box style={{ textAlign: "left" }}>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteRead}
                        disabled={isDeleteSubmitting}
                      >
                        {t("l.btn.delete.read")}
                      </Button>
                    </Box>
                  </>
                )}
              </SecurityGuard>
            </Box>
          </CardContent>
        </CardSpg>
      </form>
    </FormProvider>
  );
};

export default Feedback;
